import { useMemo } from 'react';
import type { VFC } from 'react';

import { useTranslation } from 'react-i18next';
import type { ProviderAccessMap } from '@vgtv/svp-access/lib/access_mappings';
import type { Provider } from '@vgtv/api-client';

import { OverlayMenu } from '../../OverlayMenu/OverlayMenu';
import type { MenuItem } from '../helpers';
import { Label } from '../../Label/Label';
import {
  getSubscriptionLabel,
  hasValidLoginAccess,
} from '../../../shared/helpers/subscriptionHelpers';
import { config } from '../../../config';
import { addUtmParamsToExternalLink } from '../../../shared/helpers/url';

import { UserAvatar } from './UserAvatar/UserAvatar';
import { LoginButton } from './LoginButton';

import * as styles from './UserMenu.module.scss';

export type UserMenuBaseProps = {
  accesses: ProviderAccessMap[Provider][];
  menuItems: { items: MenuItem[]; label?: string }[];
  fullName: string;
  onMenuItemClick?: () => void;
  utmParams?: Record<string, string>;
};

export const UserMenuBase: VFC<UserMenuBaseProps> = (props) => {
  const { accesses, menuItems, fullName, onMenuItemClick, utmParams } = props;
  const { t } = useTranslation();
  const subscriptionLabel =
    getSubscriptionLabel(accesses) || t('No subscription');

  const isLoggedIn = hasValidLoginAccess(accesses);

  const myPageUrl = useMemo(
    () =>
      addUtmParamsToExternalLink(config.schibstedAccount.myPageUrl, utmParams),
    [utmParams]
  );

  return (
    <div className={styles.userMenuContainer}>
      {isLoggedIn ? (
        <a href={myPageUrl} className={styles.userInfo}>
          <UserAvatar />
          <div>
            {fullName}
            <Label labels={[subscriptionLabel]} size="small" allcaps />
          </div>
        </a>
      ) : (
        <LoginButton isLoggedIn={isLoggedIn} />
      )}
      {menuItems.map(({ items, label }, index) => (
        <div key={index}>
          {label && <Label size="small" labels={[label]} allcaps></Label>}
          <OverlayMenu
            className={styles.menu}
            items={items}
            onItemClick={onMenuItemClick}
            externalLinkParams={utmParams}
          />
        </div>
      ))}
      {isLoggedIn && <LoginButton isLoggedIn={isLoggedIn} />}
    </div>
  );
};
